import { put, takeEvery, call, select } from "redux-saga/effects";

import {
  getSessionsResponse,
  getSessionDetailsResponse,
  sendMessageResponse,
  updateSessionInList,
  removeSessionFromList,
  setActiveSessionId,
  setSendingMessageFailed,
} from "./slice";

import { accessTokenResponse, loginResponse } from "../Auth/slice";
import { showSnackMsg } from "../../utils/Helpers";
import { setAdminStorage, clearAdmin } from "../../utils/Auth";
import { accessToken as refreshAccessToken } from "../../network/apis/auth";
import * as apis from "../../network/apis/agentChat";
import { getUserId } from "../../network/apis/agentChat";

export function* getSessionsSaga() {
  try {
    const response = yield call(apis.getSessions);
    const sessions = response.data.sessions.map((session) => ({
      ...session,
      lastMessage: "",
    }));
    yield put(getSessionsResponse(sessions));
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message || err.message);
  }
}

export function* getSessionDetailsSaga({ payload }) {
  try {
    const response = yield call(apis.getSessionDetails, payload);
    yield put(getSessionDetailsResponse(response.data.messages));
  } catch (err) {
    yield put(getSessionDetailsResponse([]));
    yield showSnackMsg(err.response?.data?.message || err.message);
  }
}

const parseReply = (reply) => {
  if (reply !== null && typeof reply === "object") return reply;
  if (typeof reply === "string") {
    try {
      const parsed = JSON.parse(reply);
      if (parsed !== null && typeof parsed === "object") return parsed;
    } catch {
      // plain string
    }
  }
  return null;
};

export function* sendMessageSaga({ payload }) {
  // For new sessions, generate a client-side UUID immediately so that:
  //   1. activeSessionId is set before the API responds — Stop always has
  //      a valid session ID to send to the backend.
  //   2. The session appears in the sidebar right away (optimistic UI).
  const isNewSession = !payload.sessionId;
  const clientSessionId = isNewSession ? crypto.randomUUID() : payload.sessionId;

  try {
    const userId = getUserId();

    if (!userId) {
      throw new Error("User ID not found");
    }

    if (isNewSession) {
      const optimisticTitle =
        payload.message.substring(0, 45) + (payload.message.length > 45 ? "..." : "");
      // Set activeSessionId immediately so the Stop button has a real ID.
      yield put(setActiveSessionId(clientSessionId));
      // Add the session to the sidebar right away.
      yield put(
        updateSessionInList({
          id: clientSessionId,
          title: optimisticTitle,
          lastMessage: "",
          updatedAt: new Date().toISOString(),
        })
      );
    }

    const requestPayload = { sessionId: clientSessionId, message: payload.message, userId, ...(payload.model ? { model: payload.model } : {}) };
    let response = yield call(apis.sendMessage, requestPayload);
    let { sessionId, reply } = response.data;

    // Check if the reply contains an unauthorized status
    const parsed = parseReply(reply);
    if (parsed?.status === "unauthorized") {
      try {
        const { data: tokenData } = yield call(refreshAccessToken);
        const adminData = yield select((state) => state.auth.admin);
        yield put(accessTokenResponse({ ...adminData, auth_token: tokenData.auth_token }));
        const isRememberMe = !!localStorage.getItem("admin");
        setAdminStorage(
          { ...adminData, auth_token: tokenData.auth_token },
          isRememberMe ? "local" : "session"
        );
        // Retry the original request with the refreshed token
        response = yield call(apis.sendMessage, requestPayload);
        sessionId = response.data.sessionId;
        reply = response.data.reply;
      } catch (refreshErr) {
        yield put(setSendingMessageFailed());
        yield put(loginResponse({}));
        clearAdmin();
        return;
      }
    }

    const timestamp = new Date().toISOString();
    yield put(sendMessageResponse({ sessionId, reply, timestamp }));

    // Update session preview in the sidebar.
    // If the backend returned a different ID than our client UUID, swap it out.
    const parsedFinal = parseReply(reply);
    const previewText = parsedFinal?.content ?? reply;
    const sessionTitle =
      payload.message.substring(0, 45) + (payload.message.length > 45 ? "..." : "");
    yield put(
      updateSessionInList({
        id: sessionId,
        title: sessionTitle,
        lastMessage: previewText.substring(0, 80) + (previewText.length > 80 ? "..." : ""),
        updatedAt: timestamp,
        ...(isNewSession && sessionId !== clientSessionId && { replaceId: clientSessionId }),
      })
    );
  } catch (err) {
    if (isNewSession) {
      // Clean up the optimistic session and reset activeSessionId on failure.
      yield put(removeSessionFromList(clientSessionId));
      yield put(setActiveSessionId(null));
    }
    yield put(setSendingMessageFailed());
    yield showSnackMsg(err.response?.data?.message || err.message);
  }
}

export function* stopAgentSaga({ payload }) {
  try {
    // activeSessionId is now always set before the first request fires
    // (client UUID for new sessions), so this will never be null.
    const activeSessionId = yield select((state) => state.agentChat.activeSessionId);
    const sessionId = payload.sessionId || activeSessionId;
    const response = yield call(apis.stopAgent, sessionId);
    const timestamp = new Date().toISOString();
    yield put(sendMessageResponse({ sessionId, reply: response.data, timestamp }));
  } catch (err) {
    yield put(setSendingMessageFailed());
    yield showSnackMsg(err.response?.data?.message || err.message);
  }
}

export default function* AgentChatSagas() {
  yield takeEvery("agentChat/getSessionsRequest", getSessionsSaga);
  yield takeEvery("agentChat/getSessionDetailsRequest", getSessionDetailsSaga);
  yield takeEvery("agentChat/sendMessageRequest", sendMessageSaga);
  yield takeEvery("agentChat/stopAgentRequest", stopAgentSaga);
}
